import { useState, useCallback } from 'react';
import _ from 'underscore';

const ITEMS_PER_PAGE = 10;

const MAX_ITEMS = 10;

const addItemsToPages = (pages, lastPage, lastPageIndex, count) => {
  const page = lastPage;

  if (page.numberOfAdsInPage + count > ITEMS_PER_PAGE) {
    const remaining = count - (ITEMS_PER_PAGE - page.numberOfAdsInPage);
    const itemsBefore = pages.slice(0, lastPageIndex - 1);
    const itemsAfter = pages.slice(lastPageIndex, pages.length);

    const newPageItem = { numberOfAdsInPage: ITEMS_PER_PAGE, pageNumber: page.pageNumber, id: page.id };

    const nextPageItem = { numberOfAdsInPage: 0, pageNumber: page.pageNumber + 1, id: Math.random() };

    return addItemsToPages([...itemsBefore, newPageItem, ...itemsAfter], nextPageItem, lastPageIndex + 1, remaining);
  }

  const itemsBefore = pages.slice(0, lastPageIndex - 1);
  const itemsAfter = pages.slice(lastPageIndex, pages.length);

  const newPageItem = { numberOfAdsInPage: page.numberOfAdsInPage + count, pageNumber: page.pageNumber, id: page.id };

  return {
    currentPage: newPageItem,
    pages: [...itemsBefore, newPageItem, ...itemsAfter],
  };
};

export const usePageManager = (ads, initialAdsCount = 1) => {
  const calculateTotalPages = () => {
    if (_.isUndefined(ads)) {
      return 1;
    }
    const totalPages = Math.ceil(ads.length / ITEMS_PER_PAGE);

    return totalPages;
  };

  const [totalPages, setTotalPages] = useState(calculateTotalPages());

  const maxItems = MAX_ITEMS;

  const createPages = () => {
    if (_.isUndefined(ads)) {
      return [{ numberOfAdsInPage: initialAdsCount, pageNumber: 1, id: Math.random() }];
    }

    if (ads.length === 0) {
      return [{ numberOfAdsInPage: initialAdsCount, pageNumber: 1, id: Math.random() }];
    }

    let totalAds = ads.length;

    return _.map(_.range(totalPages), (idx) => {
      const numberOfAdsInPage = totalAds > ITEMS_PER_PAGE ? ITEMS_PER_PAGE : totalAds;

      totalAds -= ITEMS_PER_PAGE;
      return { numberOfAdsInPage, pageNumber: idx + 1, id: Math.random() };
    });
  };

  const [currentPageIndex, setCurrentPageIndex] = useState(1);

  const [pages, setPages] = useState(createPages());

  const [currentPage, setCurrentPage] = useState(pages[0]);

  const getPageAdjustedIndex = (idx, pageNumber = null) => {
    const page = pageNumber || currentPageIndex;

    const val = (((page - 1) * ITEMS_PER_PAGE) + idx);

    return val;
  };

  const onAddPage = useCallback(() => {
    const newPage = { numberOfAdsInPage: 1, pageNumber: currentPageIndex + 1, id: Math.random() };

    setPages([...pages.concat(newPage)]);
    setCurrentPage(newPage);
    setCurrentPageIndex(currentPageIndex + 1);
    setTotalPages(totalPages + 1);
  }, [currentPageIndex, pages, totalPages]);

  const onAddItem = () => {
    const page = currentPage;

    if (page.numberOfAdsInPage === ITEMS_PER_PAGE) {
      onAddPage();
    } else {
      const itemsBefore = pages.slice(0, currentPageIndex - 1);
      const itemsAfter = pages.slice(currentPageIndex, pages.length);

      const newPageItem = { numberOfAdsInPage: page.numberOfAdsInPage + 1, pageNumber: page.pageNumber, id: page.id };
      setCurrentPage(newPageItem);
      setPages([...itemsBefore, newPageItem, ...itemsAfter]);
    }
  };

  const onAddItemsToPage = (count) => {
    const lastPage = pages[pages.length - 1];

    const lastPageIndex = pages.length;

    const {
      pages: newPages,
    } = addItemsToPages(pages, lastPage, lastPageIndex, count);

    setPages(newPages);
    setTotalPages(newPages.length);
    setCurrentPage(newPages[newPages.length - 1]);
    setCurrentPageIndex(newPages.length);
  };

  const goToNextPage = () => {
    setCurrentPageIndex(currentPageIndex + 1);
    setCurrentPage({ ...pages[(currentPageIndex - 1) + 1] });
  };

  const goToPrevPage = () => {
    setCurrentPageIndex(currentPageIndex - 1);
    setCurrentPage({ ...pages[(currentPageIndex - 1) - 1] });
  };

  const goToPage = (val) => {
    const valInt = parseInt(val, 10);
    if (valInt > 0 && valInt <= totalPages) {
      _.debounce(setCurrentPageIndex(parseInt(valInt, 10)), 100);
      setCurrentPage(pages[valInt - 1]);
    }
  };

  const removeLastPage = () => {
    setCurrentPage({ ...pages[(currentPageIndex - 1) - 1] });
    setPages([...pages.splice(0, pages.length - 1)]);
    setCurrentPageIndex(currentPageIndex - 1);
    setTotalPages(totalPages - 1);
  };

  const removeItemFromLastPage = () => {
    const lastPage = pages[pages.length - 1];

    if (lastPage.numberOfAdsInPage === 1) {
      removeLastPage();
    } else {
      const newPage = { id: lastPage.id, numberOfAdsInPage: lastPage.numberOfAdsInPage - 1, pageNumber: lastPage.pageNumber };

      const itemsBefore = pages.slice(0, pages.length - 1);

      setCurrentPage({ ...newPage });
      setPages([...itemsBefore, newPage]);
    }
  };

  const removeItem = () => {
    if (currentPage.numberOfAdsInPage === 1) {
      removeLastPage();
    } else if (pages.length === 1) {
      const newPage = { id: currentPage.id, numberOfAdsInPage: currentPage.numberOfAdsInPage - 1, pageNumber: currentPage.pageNumber };

      setCurrentPage({ ...newPage });
      setPages([newPage]);
    } else {
      removeItemFromLastPage();
    }
  };

  return {
    currentPage,
    currentPageIndex,
    pages,
    getPageAdjustedIndex,
    goToPrevPage,
    goToNextPage,
    goToPage,
    onAddPage,
    onAddItem,
    onAddItemsToPage,
    removeItem,
    totalPages,
    maxItems,
  };
};
